import { HTMLProps, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ControlsStatType, ControlsViewType } from "@/utils/types";
import { cn, formatValue } from "@/utils/helpers";
import { getLang, getUnit } from "@/utils/selectors";
import TooltipOverlay from "@/components/common/Tooltip/components/TooltipOverlay";
import styles from "./Tooltip.module.css";

const TOOLTIP_GAP = 20;
const TOOLTIP_PADDING = 8;
const TOOLTIP_ARROW_MARGIN = 12;

export interface TooltipProps extends Omit<HTMLProps<HTMLElement>, "title"> {
	x?: number;
	y?: number;
	show?: boolean;
	view: ControlsViewType;
	stat: ControlsStatType;
	value?: any | null;
	title?: string | null;
	subtitle?: string | null;
	footer?: string | null;
}

export default function Tooltip({
	x,
	y,
	show,
	view,
	stat,
	value,
	title,
	subtitle,
	footer,
	className
} : TooltipProps) {
	const ref = useRef<HTMLDivElement>(null);
	const [offsetX, setOffsetX] = useState(0);
	const [offsetY, setOffsetY] = useState(0);
	const [arrowOffsetX, setArrowOffsetX] = useState<number | null>(null);
	const [isShowing, setIsShowing] = useState<boolean>(false);
	const [isFlipped, setIsFlipped] = useState<boolean>(false);
	const [content, setContent] = useState({ value, title, subtitle });

	// Define unit based on current view
	const unit = useMemo(() =>
		stat && getUnit(stat)
	, [stat]);

	useLayoutEffect(() => {
		const elem = ref.current;
		if(!elem || typeof x !== "number" || typeof y !== "number") return;
		const elemWidth = elem.offsetWidth;
		const elemHeight = elem.offsetHeight;

		// Center horizontally on the anchor, then clamp within the viewport
		const centeredOffsetX = -(elemWidth / 2);
		const newOffsetX = Math.min(
			Math.max(centeredOffsetX, TOOLTIP_PADDING - x),
			window.innerWidth - TOOLTIP_PADDING - x - elemWidth
		);

		const newIsFlipped = y + TOOLTIP_GAP + elemHeight > window.innerHeight - TOOLTIP_PADDING;
		const newOffsetY = newIsFlipped ? -(elemHeight + TOOLTIP_GAP) : TOOLTIP_GAP;
		const newArrowOffsetX = Math.min(
			Math.max(-newOffsetX, TOOLTIP_ARROW_MARGIN),
			elemWidth - TOOLTIP_ARROW_MARGIN
		);

		setOffsetX(newOffsetX);
		setOffsetY(newOffsetY);
		setArrowOffsetX(newArrowOffsetX);
		setIsFlipped(newIsFlipped);
	}, [content, x, y]);

	useEffect(() => {
		// Delay hiding the tooltip after it becomes inactive until after the transition completes
		const showTimeout = setTimeout(() =>
			setIsShowing(Boolean(show))
		, show ? 0 : 50);
		// Delay resetting the tooltip content until after the transition completes
		const contentTimeout = setTimeout(() =>
			setContent({ value, title, subtitle })
		, show ? 0 : 350);
		// Set timeouts
		return () => {
			clearTimeout(showTimeout);
			clearTimeout(contentTimeout);
		}
	}, [show, value, title, subtitle]);

	const tooltip = (
		<TooltipOverlay
			ref={ref}
			footer={footer}
			className={cn(
				styles.Tooltip,
				isFlipped ? styles.Tooltip_flipped : null,
				className
			)}
			style={{
				opacity: isShowing ? 1 : 0,
				transform: x && y ?
					`translate(${x + offsetX}px, ${y + offsetY}px)`
				: "none"
			}}
		>
			<div
				className={styles.TooltipArrow}
				style={arrowOffsetX !== null ?
					{ left: arrowOffsetX }
				: undefined}
			>
				{/* Cloned from Radix Tooltip Arrow */}
				<svg
					width="10"
					height="5"
					viewBox="0 0 30 10"
					preserveAspectRatio="none"
				>
					<polygon
						points="0,0 30,0 15,10"
					/>
				</svg>
			</div>
			<div
				className={styles.TooltipTitle}
			>
				{content.title}
			</div>
			{content.subtitle ?
				<div
					className={styles.TooltipSubtitle}
				>
					{content.subtitle}
				</div>
			: null}
			<div
				className={styles.TooltipData}
			>
				<div
					className={styles.TooltipDataLabel}
				>
					{getLang(view, stat, "title")}
				</div>
				<div
					className={styles.TooltipDataValue}
				>
					{formatValue(
						content.value,
						stat
					)}
					{unit && unit !== "per" ?
						<span
							className={styles.TooltipDataValueUnit}
						>
							&nbsp;{getLang("unit", unit)}
						</span>
					: null}
				</div>
			</div>
		</TooltipOverlay>
	);
	
	return typeof document !== "undefined"
		? createPortal(tooltip, document.body)
		: null;
};